import { ChakraProvider } from '@chakra-ui/react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { DevPanel, SentryErrorBoundary } from '@payler/ui-components';

import {
  AccountPage,
  AnalyticsPage,
  AntifraudPaymentsPage,
  ApmPaymentsPage,
  AppState,
  AuthLayout,
  BaseLayout,
  ConfigProvider,
  ContractIdLayout,
  CurrencyTranslation,
  ErrorPage,
  FasterPaymentPage,
  FasterPaymentsPage,
  FasterPayoutPage,
  FasterPayoutsPage,
  MakeFasterPayoutPage,
  GoogleReCaptchaComp,
  GoogleTagManager,
  InvoicesPage,
  LoginPage,
  LogoutPage,
  NotFoundPage,
  NotificationsPage,
  PaymentPage,
  PaymentSettingsPage,
  PaymentsPage,
  PayoutsPage,
  queryClient,
  RegisterConfirmPage,
  ReportsPage,
  ResetPasswordPage,
  SetNewPasswordPage,
  SettingsLayout,
  theme,
  useIsAuthorized,
  useNavigateRef,
  shouldEnableSentry,
  UserCultureWatcher,
  UsersPage,
  WithdrawalsPage,
  ApmPaymentSessionDetail,
  BulkPayoutDetailsPage,
  BulkRefundDetailsPage,
  CryptoPaymentSessionDetail,
  CryptoPaymentsPage,
  PasswordUpdatePage,
  ReturnsPage,
} from '../../../../libs/merchant-cabinet/ui/src';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { environment } from '../environments/environment';
import { GlobalFonts } from '@payler/ui-theme';
import { useEffect, useRef } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { useContracts, useHasAnyCardContract } from '../../../../libs/merchant-cabinet/ui/src/lib/hooks/contracts';

export const App = () => {
  const enableSentry = shouldEnableSentry(environment.environment);

  return (
    <SentryErrorBoundary errorPage={<ErrorPage />} enableSentry={enableSentry}>
      <ConfigProvider env={environment}>
        <GoogleTagManager />
        <AppState>
          <QueryClientProvider client={queryClient}>
            <HelmetProvider>
              <Helmet title="Merchant Cabinet" />
              <ChakraProvider theme={theme}>
                <GlobalFonts />
                <GoogleReCaptchaComp>
                  <BrowserRouter>
                    <AuthWatcher />
                    <AppRoutes />
                  </BrowserRouter>
                  <DevPanel />
                </GoogleReCaptchaComp>
              </ChakraProvider>
            </HelmetProvider>
          </QueryClientProvider>
        </AppState>
      </ConfigProvider>
    </SentryErrorBoundary>
  );
};

const AppRoutes = () => {
  const location = useLocation();
  const authorized = useIsAuthorized();
  const contracts = useContracts(authorized);
  const hasAnyCardContract = useHasAnyCardContract(authorized);

  if (!authorized) {
    const redirectParam = location?.pathname === '/password/update'
      ? `?redirect=${encodeURIComponent(location.pathname + location.search)}`
      : '';

    return (
      <Routes>
        <Route path="auth" element={<AuthLayout />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="resetPassword" element={<ResetPasswordPage />} />
        </Route>
        <Route path="recovery" element={<AuthLayout />}>
          <Route path="" element={<SetNewPasswordPage />} />
        </Route>
        <Route path="confirm" element={<AuthLayout />}>
          <Route path="" element={<RegisterConfirmPage />} />
        </Route>
        <Route path="*" element={<Navigate to={`/auth/login${redirectParam}`} />} />
      </Routes>
   );
  }

  const firstContractId = contracts?.[0]?.id;

  const isLoading = !contracts || hasAnyCardContract === undefined;

  return (
    <>
      <UserCultureWatcher />
      <CurrencyTranslation />
      <Routes>
        <Route path="/" element={<BaseLayout />}>
          <Route
            index
            element={
              isLoading ? null : hasAnyCardContract ? (
                <AnalyticsPage />
              ) : (
                <Navigate to={`/${firstContractId}/pay`} replace />
              )
            }
          />
          <Route path="reports" element={<ReportsPage />} />
          <Route path=":contractId" element={<ContractIdLayout />}>
            <Route path="pay" element={<PaymentsPage />} />
            <Route path="pay/:id" element={<PaymentPage mode="payment" />} />

            <Route path="alerts" element={<AntifraudPaymentsPage />} />
            <Route
              path="alerts/:id"
              element={<PaymentPage mode="antifraud" />}
            />

            <Route path="credit" element={<PayoutsPage />} />
            <Route path="credit/:id" element={<PaymentPage mode="payout" />} />

            <Route path="bill" element={<InvoicesPage />} />
            <Route path="bill/:id" element={<PaymentPage mode="bill" />} />

            <Route
              path="makecredit"
              element={<WithdrawalsPage mode="single" />}
            />
            <Route
              path="makebulkpayout"
              element={<WithdrawalsPage mode="bulkPayout" />}
            />
            <Route
              path="makecredit/:id"
              element={<PaymentPage mode="makecredit" />}
            />
            <Route
              path="makebulkpayout/:id"
              element={<BulkPayoutDetailsPage />}
            />

            <Route path="faster-payouts" element={<FasterPayoutsPage />} />
            <Route path="faster-payouts/:id" element={<FasterPayoutPage />} />
            <Route
              path="makefasterpayout"
              element={<MakeFasterPayoutPage />}
            />

            <Route path="faster-payments" element={<FasterPaymentsPage />} />
            <Route path="faster-payments/:id" element={<FasterPaymentPage />} />

            <Route path="apm-payments" element={<ApmPaymentsPage />} />
            <Route
              path="apm-payments/:id"
              element={<ApmPaymentSessionDetail />}
            />

            <Route path="crypto-payments" element={<CryptoPaymentsPage />} />
            <Route
              path="crypto-payments/:id"
              element={<CryptoPaymentSessionDetail />}
            />

            <Route path="makereturn" element={<ReturnsPage />} />
            <Route
              path="makereturn/:id"
              element={<BulkRefundDetailsPage />}
            />

            <Route path="*" element={<NotFoundPage />} />
          </Route>
          <Route path="settings" element={<SettingsLayout />}>
            <Route path="account" element={<AccountPage />} />
            <Route path="payment" element={<PaymentSettingsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route index element={<Navigate to="account" replace />} />
          </Route>
          <Route path="logout" element={<LogoutPage />} />
          <Route path="auth/*" element={<Navigate to="/" />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="password" element={<AuthLayout />}>
          <Route path="update" element={<PasswordUpdatePage />} />
        </Route>
      </Routes>
    </>
  );
};

const AuthWatcher = () => {
  const authorized = useIsAuthorized();
  const navigate = useNavigateRef();
  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
    } else {
      navigate.current(authorized ? '/' : '/auth/login');
    }
  }, [authorized, navigate]);

  return null;
};

export default App;
